import Nuxeo from 'nuxeo'
import { axiosPostOrderNumbers } from '@/composables/api'
import {
    NuxeoRegistrationRequest,
    NuxeoActivationRequest,
    NuxeoGetByEmailRequest,
    NuxeoUpdatePasswordRequest,
    NuxeoRequestConfig,
    NuxeoSearchRequest,
    NuxeoCalSearchRequest
} from '@/contracts/api/RequestContracts'
import {
    NuxeoRegistrationResponse,
    NuxeoRetrieveDocumentResponse,
    NuxeoActivationResponse,
    NuxeoGetByEmailResponse,
    PortalLoginResponse,
    PortalResetPasswordResponse,
    NuxeoUpdatePasswordResponse,
    NuxeoPageProviderResponse,
    NuxeoErrorResponse,
    PortalConfigFromNuxeoResponse
} from '@/contracts/api/ResponseContracts'
import { NuxeoErrorMap } from '@/contracts/api/StructureContracts'
import router from '@/./router'
import store from '@/./store'

// Get Notice Details:
export function getPortalConfigFromNuxeo(callback: (response: PortalConfigFromNuxeoResponse) => void): void
{
    executeAutomation('GetPortalConfigurationOperation')
        .execute()
        .then(function(response: PortalConfigFromNuxeoResponse)
{
            // Log in non-production:
            if(process.env.NODE_ENV !== 'production')
{
                console.log(response)
            }
            callback(response)
        })
        .catch(handleError)
}

// Nuxeo registration:
export function nuxeoRegistration(params: NuxeoRegistrationRequest, callback: (response: NuxeoRegistrationResponse) => void): void
{
    executeAutomation('RegisterExternalUserOperation')
        .params(params)
        .execute()
        .then(function(response: NuxeoRegistrationResponse)
{
            // Log in non-production:
            if(process.env.NODE_ENV !== 'production')
{
                console.log(response)
            }
            callback(response)
        })
        .catch(handleError)
}

// Nuxeo get document:
export function nuxeoRetrieveDocument(request: string, headers: Record<string, string>, callback: (response: NuxeoRetrieveDocumentResponse) => void): void
{
    executeRequest(request,
        {
            headers: headers
        })
        .get()
        .then(function(response: NuxeoRetrieveDocumentResponse)
{
            // Log in non-production:
            if(process.env.NODE_ENV !== 'production')
{
                console.log(response)
            }
            callback(response)
        })
        .catch(handleError)
}

// Nuxeo activation:
export function nuxeoActivation(params: NuxeoActivationRequest, callback: (response: NuxeoActivationResponse) => void): void
{
    executeAutomation('ActivateExternalUserOperation')
        .params(params)
        .execute()
        .then(function(response: NuxeoActivationResponse)
{
            // Log in non-production:
            if(process.env.NODE_ENV !== 'production')
{
                console.log(response)
            }
            callback(response)
        })
        .catch(handleError)
}

// Nuxeo get user by email:
export function nuxeoGetByEmail(params: NuxeoGetByEmailRequest, callback: (response: NuxeoGetByEmailResponse) => void, customMaps?: Array<NuxeoErrorMap>): void
{
    // Set error maps:
    const maps =
        [
            {
                code: 404,
                error: '404 returned for',
                message: 'Your email address could not be found as your activation link has expired. Please create an account and activate within 2 days.'
            },
            {
                code: 404,
                error: 'Failed to find user: email',
                message: 'Email not found.'
            }
        ]
    executeAutomation('FindExternalUserOperation')
        .params(params)
        .execute()
        .then(function(response: NuxeoGetByEmailResponse)
{
            // Log in non-production:
            if(process.env.NODE_ENV !== 'production')
{
                console.log(response)
            }
            callback(response)
        })
        .catch(function(error: NuxeoErrorResponse)
{
            handleError(error, maps, customMaps)
        })
}

// Portal login — POSTs the credentials to the PHP proxy's /auth/login
// endpoint, which verifies them against Nuxeo and then runs the RFC 7523
// JWT-bearer token exchange server-side (the assertion-signing secret and
// OAuth client secret never reach the browser — Q18/Q22 in
// docs/proposals/irm-questions-register.md). The response carries the Nuxeo
// user entity plus the OAuth token pair.
export function nuxeoLogin(username: string, password: string, callback: (response: PortalLoginResponse) => void, customMaps?: Array<NuxeoErrorMap>): void
{
    // Set error maps:
    const maps =
        [
            {
                code: 404,
                error: 'HTTP/1.1 401',
                message: 'Account not found.'
            }
        ]
    fetch(process.env.VUE_APP_API_DOMAIN + '/auth/login',
        {
            method: 'POST',
            headers:
            {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ username: username, password: password })
        })
        .then(function(response: Response)
{
            // Mirror the nuxeo-js error shape (an error carrying .response) so
            // handleError and the existing error maps keep working:
            if(!response.ok)
{
                return Promise.reject(Object.assign(new Error('HTTP ' + response.status), { response: response }))
            }
            return response.json()
        })
        .then(function(response: PortalLoginResponse)
{
            // Log in non-production:
            if(process.env.NODE_ENV !== 'production')
{
                console.log(response)
            }
            callback(response)
        })
        .catch(function(error: NuxeoErrorResponse)
{
            handleError(error, maps, customMaps)
        })
}

// UpdateExternalUserOperation is no longer called from the browser: the reset
// confirm goes through the proxy's /auth/reset-password route and account
// self-service through /account/update-user (both validate server-side, run
// under the service account, and the op is blacklisted from the pass-through).

// Nuxeo update password:
export function nuxeoUpdatePassword(params: NuxeoUpdatePasswordRequest, callback: (response: NuxeoUpdatePasswordResponse) => void, customMaps?: Array<NuxeoErrorMap>): void
{
    // Set error maps:
    const maps =
        [
            {
                code: 409,
                error: 'Failed to reset password: email',
                message: 'The email address ' + params.email + ' has an outstanding password reset request'
            }
        ]
    executeAutomation('ResetExternalUserPasswordOperation')
        .params(params)
        .execute()
        .then(function(response: NuxeoUpdatePasswordResponse)
{
            // Log in non-production:
            if(process.env.NODE_ENV !== 'production')
{
                console.log(response)
            }
            callback(response)
        })
        .catch(function(error: NuxeoErrorResponse)
{
            handleError(error, maps, customMaps)
        })
}

// Nuxeo orders: moved to the PHP proxy's fail-closed /orders route
// (axiosPostOrders in api.ts) — GetAllOrderedGrantsOperation must not be
// reachable through the anonymous pass-through (register Q34; IRM's
// 2026-07-15 signed-out-calls report). The op-behaviour notes now live on that
// route in php/public/index.php: it returns a proper paginated
// PaginableDocumentType and honours paging/sort/filter params (Q31, IRM's
// 2026-08-24 rework), needs X-NXproperties '*' for file:content (Q30), and
// once fail-OPENed on a non-matching customerID (register Q38 — fixed by IRM
// 2026-09-07; the route still pins customerID to the verified token and keeps
// ordersOwnershipGuard as a regression guard).

// Nuxeo search:
export function nuxeoSearch(params: NuxeoSearchRequest, callback: (response: NuxeoPageProviderResponse) => void): void
{
    const nuxeo = initNuxeo()
    nuxeo.repository(
        {
            headers:
            {
                properties: 'hmcts_grant_schema',
                skipAggregates: true
            }
        })
        .query(Object.assign(
            {
                pageProvider: 'pp_mainstream_default_search'
            }, params))
        .then(function(response: NuxeoPageProviderResponse)
{
            // Log in non-production:
            if(process.env.NODE_ENV !== 'production')
{
                console.log(response)
            }
            callback(response)
        })
        .catch(handleError)
}

// Nuxeo cal search:
export function nuxeoCalSearch(params: NuxeoCalSearchRequest, callback: (response: NuxeoPageProviderResponse) => void): void
{
    const nuxeo = initNuxeo()
    nuxeo.repository(
        {
            headers:
            {
                properties: 'hmcts_calendarindex_schema,file',
                'fetch-document': 'properties',
                skipAggregates: true
            }
        })
        .query(Object.assign(
            {
                pageProvider: 'pp_cowindex_default_search'
            }, params))
        .then(function(response: NuxeoPageProviderResponse)
{
            // Log in non-production:
            if(process.env.NODE_ENV !== 'production')
{
                console.log(response)
            }
            callback(response)
        })
        .catch(handleError)
}

// Nuxeo order creation moved server-side to the PHP /payment/prepare endpoint
// (CreatePendingOrderOperation). The former nuxeoOrderComplete() helper — which
// called the now-removed javascript.AS_API_Create_Order_From_Website — has been
// retired; the browser no longer creates orders (§2.2 / §3.8). The calendar
// order -> grant conversion (RequestNewWillOperation) likewise moved server-side
// into /payment/prepare, so the former client-side nuxeoCalCreateGrant() helper
// has been retired too (register Q5/Q33).

// Nuxeo get customer orders — the signed-in customer's own order numbers, used
// by My Orders to attribute each grant's ordersState rows to the caller (a grant
// may be shared across customers). Now routed through the proxy's fail-closed
// /order-numbers endpoint (axiosPostOrderNumbers): the query runs SERVER-SIDE
// with customerID pinned to the verified token. The former client-built NXQL
// string — sent with no token and lent the admin service account by the
// default-open pass-through — is gone (IRM security review 2026-09-24). The
// customer is derived from the token server-side, so no username is sent.
export function nuxeoCustomerOrders(callback: (response: NuxeoPageProviderResponse) => void): void
{
    axiosPostOrderNumbers(callback)
}

// Password reset confirm — POST the emailed token + new password to the PHP
// proxy's /auth/reset-password endpoint. The proxy validates the token, the
// 1-hour expiry and single-use SERVER-SIDE, then performs the update under the
// service account. This replaces the former browser-direct flow, which looked
// the reset document up with a client-built NXQL query (token injectable) and
// called UpdateExternalUserOperation unauthenticated (lent the admin service
// account — an account-takeover primitive). The proxy returns a user-facing
// `message` on failure, surfaced by handleError.
export function nuxeoResetPassword(token: string, password: string, callback: (response: PortalResetPasswordResponse) => void): void
{
    fetch(process.env.VUE_APP_API_DOMAIN + '/auth/reset-password',
    {
        method: 'POST',
        headers:
        {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: token, password: password })
    })
    .then(function(response: Response)
    {
        // Mirror the nuxeo-js error shape (an error carrying .response) so
        // handleError can read the proxy's message body:
        if(!response.ok)
        {
            return Promise.reject(Object.assign(new Error('HTTP ' + response.status), { response: response }))
        }
        return response.json()
    })
    .then(function(response: PortalResetPasswordResponse)
    {
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
        {
            console.log(response)
        }
        callback(response)
    })
    .catch(handleError)
}

// Nuxeo documents downloaded (CheckAndSetExpiryDateForOrderItemsOperation):
// We pass the grant UID as the operation INPUT and collectionID as a param. dit02
// op-def (2026-07-01) confirms a document-input signature with documentUid as an
// OPTIONAL param; IRM's 14A/14B examples instead pass documentUid as a param. Left
// as-is (the input path is supported) pending IRM confirmation — see register Q32.

// Nuxeo documents downloaded:
// Init nuxeo client:
function initNuxeo()
{
    return new Nuxeo(
        {
            baseURL: process.env.VUE_APP_API_DOMAIN
        })
}

// Execute automation:
function executeAutomation(operation: string): any
{
    const nuxeo = initNuxeo()
    return nuxeo.operation(operation)
}

// Execute request:
function executeRequest(request: string, config: NuxeoRequestConfig): any
{
    const nuxeo = initNuxeo()
    return nuxeo.request(request, config)
}

// Handle error in axios:
function handleError(error: NuxeoErrorResponse, errorMappings?: Array<NuxeoErrorMap>, customErrorMappings?: Array<NuxeoErrorMap>): void
{
    // Concat custom maps:
    if(errorMappings !== undefined && customErrorMappings !== undefined)
{
        errorMappings = customErrorMappings.concat(errorMappings)
    }
    // Look for response in error:
    if(error.response)
{
        // Log in non-production:
        if(process.env.NODE_ENV !== 'production')
{
            console.log(error.response)
        }
        error.response.text().then(function(body)
{
            // Set error:
            const errorMesssage = JSON.parse(body)
            // Check status to override defaults:
            switch(error.response.status)
{
                case 500:
                    errorMesssage.message = 'HTTP/1.1 500 returned - please contact your administrator.'
                    break
                case 405:
                    errorMesssage.message = 'HTTP/1.1 405 returned - method not allowed.'
                    break
            }
            // Look for mappings:
            if(errorMappings !== undefined)
{
                errorMappings.every(function(map)
{
                    console.log(errorMesssage.message, map.error)
                    if(errorMesssage.message.includes(map.error))
{
                        errorMesssage.message = map.message
                        if(map.field !== undefined)
{
                            errorMesssage.field = map.field
                        }
                        return false
                    }
                    return true
                })
            }
            // Display error:
            store.commit('errors/setNuxeoErrors', errorMesssage)
            router.push({ hash: '#error-summary-container' })
        })
    }
}
